/*
 * PRD-037 — Primitivos UI del portal del partner (app SEPARADA).
 * Diseño V4: tokens reales de @components/v4/tokens (sin Tailwind, inline styles), Poppins,
 * cards blancas, botones UPPERCASE. No depende de componentes con estado del panel.
 */
import type { ButtonHTMLAttributes, CSSProperties, InputHTMLAttributes, ReactNode } from 'react';
import { V4 } from '@components/v4/tokens';

export const C = {
  primary: V4.primary,
  secondary: V4.secondary,
  secondaryDark: V4.secondaryDark,
  secondaryLight: V4.secondaryLight,
  success: V4.success,
  danger: V4.danger,
  warning: V4.warning,
  white: V4.white,
  pageBg: V4.grayBlueLight, // #F2F6F8
  cardBorder: V4.grayLight, // #E5ECEE
  muted: V4.grayUltraDark, // #6B6B6B
  inputBorder: V4.gray, // #BFD3D9
};

const FONT = 'Poppins, sans-serif';

export const PageShell = ({ children, narrow }: { children: ReactNode; narrow?: boolean }) => (
  <div style={{ minHeight: '100vh', background: C.pageBg, fontFamily: FONT, color: C.primary, padding: '24px 16px' }}>
    <div style={{ maxWidth: narrow ? 460 : 760, margin: '0 auto' }}>{children}</div>
  </div>
);

export const Card = ({ children, style }: { children: ReactNode; style?: CSSProperties }) => (
  <div
    style={{
      background: C.white,
      border: `1px solid ${C.cardBorder}`,
      borderRadius: 12,
      padding: 24,
      marginBottom: 16,
      boxSizing: 'border-box',
      ...style,
    }}
  >
    {children}
  </div>
);

export const Title = ({ children, sub }: { children: ReactNode; sub?: ReactNode }) => (
  <div style={{ marginBottom: sub ? 18 : 14 }}>
    <h1 style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.01em', margin: 0 }}>{children}</h1>
    {sub ? <p style={{ fontSize: 13, color: C.muted, margin: '4px 0 0' }}>{sub}</p> : null}
  </div>
);

export const SectionTitle = ({ children }: { children: ReactNode }) => (
  <h2 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 14px' }}>{children}</h2>
);

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost';
  fullWidth?: boolean;
};
export const Button = ({ variant = 'primary', fullWidth, style, disabled, children, ...rest }: ButtonProps) => {
  const base: CSSProperties = {
    fontFamily: FONT,
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
    height: 38,
    padding: '0 18px',
    borderRadius: 6,
    cursor: disabled ? 'default' : 'pointer',
    border: '1.5px solid transparent',
    width: fullWidth ? '100%' : undefined,
    opacity: disabled ? 0.35 : 1,
    pointerEvents: disabled ? 'none' : undefined,
  };
  const variants: Record<string, CSSProperties> = {
    primary: { background: C.secondary, color: C.white, borderColor: C.secondary },
    secondary: { background: C.white, color: C.primary, borderColor: C.primary },
    ghost: { background: 'transparent', color: C.secondary, borderColor: C.secondary },
  };
  return (
    <button style={{ ...base, ...variants[variant], ...style }} disabled={disabled} {...rest}>
      {children}
    </button>
  );
};

type FieldProps = InputHTMLAttributes<HTMLInputElement> & { label: string };
export const Field = ({ label, style, ...rest }: FieldProps) => (
  <label style={{ display: 'block', marginBottom: 14 }}>
    <span style={{ display: 'block', fontSize: 12, fontWeight: 500, color: C.primary, marginBottom: 6 }}>{label}</span>
    <input
      style={{
        width: '100%',
        height: 38,
        padding: '0 12px',
        fontSize: 14,
        fontFamily: FONT,
        color: C.primary,
        border: `1px solid ${C.inputBorder}`,
        borderRadius: 6,
        boxSizing: 'border-box',
        outline: 'none',
        ...style,
      }}
      {...rest}
    />
  </label>
);

// Error inline de campo (debajo del input), distinto del ErrorNote de bloque.
export const FieldError = ({ children }: { children: ReactNode }) => (
  <span style={{ display: 'block', fontSize: 11, color: C.danger, marginTop: 6 }}>{children}</span>
);

// Campo de WhatsApp con lada +52 fija y captura de 10 digitos nacionales (espeja el registro).
type PhoneFieldProps = {
  label: string;
  value: string; // solo los 10 digitos nacionales
  onChange: (digits: string) => void;
  error?: string | null;
  required?: boolean;
};
export const PhoneField = ({ label, value, onChange, error, required }: PhoneFieldProps) => (
  <label style={{ display: 'block', marginBottom: 14 }}>
    <span style={{ display: 'block', fontSize: 12, fontWeight: 500, color: C.primary, marginBottom: 6 }}>
      {label}
      {required ? ' *' : ''}
    </span>
    <div style={{ display: 'flex' }}>
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          height: 38,
          padding: '0 10px',
          fontSize: 14,
          fontFamily: FONT,
          color: C.muted,
          background: C.pageBg,
          border: `1px solid ${error ? C.danger : C.inputBorder}`,
          borderRight: 'none',
          borderRadius: '6px 0 0 6px',
        }}
      >
        +52
      </span>
      <input
        inputMode="numeric"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 10))}
        placeholder="10 digitos"
        style={{
          flex: 1,
          minWidth: 0,
          height: 38,
          padding: '0 12px',
          fontSize: 14,
          fontFamily: FONT,
          color: C.primary,
          border: `1px solid ${error ? C.danger : C.inputBorder}`,
          borderRadius: '0 6px 6px 0',
          boxSizing: 'border-box',
          outline: 'none',
        }}
      />
    </div>
    {error ? <FieldError>{error}</FieldError> : null}
  </label>
);

export const Badge = ({ tone, children }: { tone: 'draft' | 'claimed'; children: ReactNode }) => (
  <span
    style={{
      display: 'inline-block',
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '0.02em',
      padding: '2px 10px',
      borderRadius: 9999,
      color: tone === 'claimed' ? C.success : C.secondaryDark,
      background: tone === 'claimed' ? '#D1FFD0' : '#D0F2FF',
    }}
  >
    {children}
  </span>
);

export const ErrorNote = ({ children }: { children: ReactNode }) => (
  <p
    style={{
      color: C.danger,
      fontSize: 13,
      margin: '12px 0 0',
      background: '#FFD8D8',
      padding: '8px 12px',
      borderRadius: 6,
    }}
  >
    {children}
  </p>
);

export const Muted = ({ children, style }: { children: ReactNode; style?: CSSProperties }) => (
  <p style={{ fontSize: 13, color: C.muted, margin: '8px 0 0', ...style }}>{children}</p>
);
